import React, { useRef, useEffect, useState } from 'react';
import { LocationMatrixHelper } from "@swivl-mono/helpers";
import { LocationScope, MemberPermission } from "@swivl-mono/types";
import { Panel, Spinner, Switch } from "@swivl-mono/ui";
import LocationsModel from "../../../Models/Locations/Locations.Model";
import { Member } from "../../../Models/Member/Member.Current";

interface LocationPickerOptionProps {
  on: boolean;
  label: any;
  indent: string;
  onChange: (event: any) => void;
}

function LocationPickerOption(props: LocationPickerOptionProps) {
  return (
    <div className="flex flex-row w-full mb-3">
      <div className={"flex-1 " + props.indent}>{props.label}</div>
      <div className="flex col">
        <Switch on={props.on} onChange={props.onChange} />
      </div>
    </div>
  );
}

interface LocationPickerProps {
  locationPermission: MemberPermission;
  updatePermissions: (updated: MemberPermission) => void;
}

export const LocationPicker: React.FC<LocationPickerProps> = ({
  locationPermission,
  updatePermissions
}) => {
  const { groups } = LocationsModel.useState();
  const locations = locationPermission.locations || {};
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  let message = "";

  const orgWide = locations.orgId === Member.orgId;

  // Handle click outside to close panel
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (orgWide) {
    message = "All locations";
  } else if (groups) {
    let locationsEnabled = 0;
    Object.values(groups).map((group) => {
      if (locations.groupIds?.includes(group.id)) {
        locationsEnabled += group.locations.length;
      } else if (group.locations) {
        locationsEnabled += group.locations.filter((location) => locations.locationIds?.includes(location.id)).length;
      }
    });
    message = locationsEnabled > 0 ? `Filtered by ${locationsEnabled} ${locationsEnabled === 1 ? 'location' : 'locations'}` : "No locations selected";
  } else {
    message = "No locations selected";
  }

  if (!groups) {
    return <Panel style={Panel.Style.GREY} header={<Spinner />} />;
  }

  return (
    <div ref={panelRef}>
      <Panel 
        style={Panel.Style.GREY} 
        header={message}
        open={isOpen}
        onToggle={() => setIsOpen(!isOpen)}
      >
        <div className="flex flex-col">
        <LocationPickerOption
          key="all"
          on={orgWide}
          label="All"
          indent=""
          onChange={(event) => {
            updatePermissions({
              ...locationPermission,
              locations: LocationMatrixHelper.updateMatrix(
                event.target.checked,
                LocationScope.ORG,
                Member.orgId,
                locationPermission.locations,
                groups,
              ),
            });
          }}
        />
        {groups &&
          Object.values(groups).map((group) => {
            const groupWide = locations.groupIds?.includes(group.id) ? true : false;

            return (
              <div key={"container-" + group.id}>
                <LocationPickerOption
                  on={orgWide || groupWide}
                  label={group.name}
                  key={"group-" + group.id}
                  indent="pl-4"
                  onChange={(event) => {
                    updatePermissions({
                      ...locationPermission,
                      locations: LocationMatrixHelper.updateMatrix(
                        event.target.checked,
                        LocationScope.GROUP,
                        group.id,
                        locationPermission.locations,
                        groups,
                      ),
                    });
                  }}
                />
                {group.locations.map((location) => {
                  const locationWide = locations.locationIds?.includes(location.id) ? true : false;

                  return (
                    <LocationPickerOption
                      on={orgWide || groupWide || locationWide}
                      label={<>{location.name}</>}
                      indent="pl-8"
                      key={"location-" + location.id}
                      onChange={(event) => {
                        updatePermissions({
                          ...locationPermission,
                          locations: LocationMatrixHelper.updateMatrix(
                            event.target.checked,
                            LocationScope.LOCATION,
                            location.id,
                            locationPermission.locations,
                            groups,
                          ),
                        });
                      }}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      </Panel>
    </div>
  );
};